import { z } from "zod";

/* ===========================================================================
 * QĐ người dùng 27/10/2026 (@UC9516 @UC9594) — NHÓM PHỤ HUYNH: MỘT NGƯỜI ĐẠI DIỆN NHIỀU SUẤT, MỘT SUẤT NHIỀU NGƯỜI ĐẠI DIỆN.
 *
 * Hai mảnh, một luồng:
 *  1. `POST /v1/groups/:id/invitations/identity-selection/batch` — người cầm link lớp chọn NHIỀU suất có sẵn + thêm NHIỀU suất mới
 *     trong MỘT lượt, MỘT transaction (đủ cả hoặc không gì). Endpoint đơn (`identity-selection`) giữ nguyên cho app bản cũ.
 *  2. Suất ĐÃ có người đại diện ⇒ KHÔNG gắn liên kết ngay (liên kết sống = có quyền đọc/khai tiền cho suất đó) mà ghi một LỜI XIN chờ
 *     O/A duyệt: `GET/POST /v1/groups/:id/representative-requests[/approve|/reject]`.
 * ========================================================================= */

/* ---------------------------------------------------------------------------
 * Phía O/A — danh sách + duyệt/từ chối lời xin đại diện.
 * ------------------------------------------------------------------------- */

export const REPRESENTATIVE_REQUESTS_MAX_LIMIT = 50;
export const REPRESENTATIVE_REQUESTS_MAX_CURSOR_LENGTH = 512;
export const REPRESENTATIVE_REQUEST_IDS_MAX = 100;

export const REPRESENTATIVE_REQUEST_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const;

export const REPRESENTATIVE_REQUEST_CODE = {
  /**
   * Phía NGƯỜI DUYỆT: suất của lời xin không còn nhận người đại diện (đã bị xoá / trả về trống / không còn vai `member`). 409, cả lượt
   * rollback — không tự quyết thay O/A. Lối ra: từ chối lời xin (người xin mở lại link nếu muốn).
   */
  TARGET_UNAVAILABLE: "REPRESENTATIVE_REQUEST_TARGET_UNAVAILABLE",
} as const;
export const REPRESENTATIVE_REQUEST_TARGET_UNAVAILABLE_MESSAGE =
  "Suất này không còn nhận người đại diện — hãy từ chối lời xin.";

/**
 * Một lời xin đang chờ. Tên người xin lấy từ hồ sơ người dùng (`users.fullName`) — người xin CHƯA có liên kết nào trong nhóm nên chưa có
 * tên-trong-nhóm. `requesterName` null khi hồ sơ không có tên (đã ẩn danh) ⇒ client tự có nhãn thay thế.
 */
export const representativeRequestItemSchema = z
  .object({
    id: z.string().uuid(),
    targetMemberId: z.string().uuid(),
    targetDisplayName: z.string().nullable(),
    requesterUserId: z.string().uuid(),
    requesterName: z.string().nullable(),
    requesterPublicHandle: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();

export const representativeRequestsListResponseSchema = z
  .object({
    data: z.array(representativeRequestItemSchema),
    pageInfo: z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }).strict(),
  })
  .strict();

export const representativeRequestsListQuerySchema = z.object({
  cursor: z.string().max(REPRESENTATIVE_REQUESTS_MAX_CURSOR_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(REPRESENTATIVE_REQUESTS_MAX_LIMIT).optional(),
});

/** Body duyệt/từ chối — id LỜI XIN (không phải id suất). */
export const representativeRequestDecisionSchema = z
  .object({ requestIds: z.array(z.string().uuid()).min(1).max(REPRESENTATIVE_REQUEST_IDS_MAX) })
  .strict();

export type RepresentativeRequestItem = z.infer<typeof representativeRequestItemSchema>;
export type RepresentativeRequestsListResponse = z.infer<typeof representativeRequestsListResponseSchema>;
export type RepresentativeRequestsListQuery = z.infer<typeof representativeRequestsListQuerySchema>;
export type RepresentativeRequestDecision = z.infer<typeof representativeRequestDecisionSchema>;
